import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Typography, Row, Col, Input } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { GET_TALENTS } from '../../queries';
import { UPDATE_TALENT } from '../../mutations';

const TalentManagement = () => {
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [selectedTalent, setSelectedTalent] = useState(null);
    const [filteredTalents, setFilteredTalents] = useState([]);

    const { loading, data, refetch } = useQuery(GET_TALENTS, {
        variables: { isVisible: false },
        fetchPolicy: 'network-only',
        onError: (error) => {
            message.error('削除されたタレントの取得に失敗しました');
            console.error('Error fetching deleted talents:', error);
        }
    });

    useEffect(() => {
        if (data) {
            const talents = data.talents.edges.map(edge => edge.node);
            setFilteredTalents(talents);
        }
    }, [data]);

    const [updateTalent] = useMutation(UPDATE_TALENT, {
        onCompleted: () => {
            message.success('タレントを復元しました');
            refetch();
        },
        onError: (error) => {
            message.error('タレントの復元に失敗しました');
            console.error('Error recovering talent:', error);
        }
    });

    const handleRecoverTalent = async (talentId) => {
        try {
            await updateTalent({
                variables: {
                    input: {
                        id: talentId,
                        isVisible: true
                    }
                }
            });
        } catch (error) {
            // Error is handled in onError callback
        }
    };

    const confirmRecover = (talent) => {
        setSelectedTalent(talent);
        setConfirmModalVisible(true);
    };

    const handleConfirm = () => {
        if (selectedTalent) {
            handleRecoverTalent(selectedTalent.id);
        }
        setConfirmModalVisible(false);
        setSelectedTalent(null);
    };

    const handleSearch = (searchField) => {
        const searchValue = searchField.target.value.trim().toLowerCase();
        if (searchValue === '') {
            setFilteredTalents(data.talents.edges.map(edge => edge.node));
        } else {
            setFilteredTalents(
                data.talents.edges
                    .map(edge => {
                        const talent = edge.node;
                        const assignedUsers = talent.users || [];
                        const userNames = assignedUsers
                            .map(user => `${user.firstName}${user.lastName}`.toLowerCase())
                            .join(' ');
                        
                        if (
                            talent.name.toLowerCase().includes(searchValue) ||
                            userNames.includes(searchValue)
                        ) {
                            return talent;
                        }
                        return null;
                    })
                    .filter(Boolean)
            );
        }
    };

    const columns = [
        {
            title: <Typography.Text strong>氏名</Typography.Text>,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: <Typography.Text strong>担当者</Typography.Text>,
            key: 'users',
            render: (_, record) => {
                const users = record.users || [];
                return users.map(user => `${user.firstName} ${user.lastName}`).join(', ') || '-';
            }
        },
        {
            title: <Typography.Text strong>操作</Typography.Text>,
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        onClick={() => confirmRecover(record)}
                    >
                        復元
                    </Button>
                </Space>
            ),
        },
    ];

    const TableHeader = (
        <Row>
            <Col span={4}>
                <Input.Search
                    onChange={handleSearch}
                    allowClear
                    placeholder="検索 (氏名または担当者)"
                />
            </Col>
        </Row>
    );

    return (
        <>
            <Table
                columns={columns}
                dataSource={filteredTalents}
                loading={loading}
                rowKey="id"
                title={() => TableHeader}
                pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `全 ${total} 件`
                }}
            />
            <Modal
                title="タレントの復元"
                open={confirmModalVisible}
                onOk={handleConfirm}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    setSelectedTalent(null);
                }}
                okText="復元"
                cancelText="キャンセル"
            >
                {selectedTalent && `${selectedTalent.name}を復元しますか？`}
            </Modal>
        </>
    );
};

export default TalentManagement; 